import { InfiniteData } from '@tanstack/react-query';
import {
  GetUserListResponse,
  GetUsersByDepartmentResponse,
  User,
  UserResponse,
} from 'compliance-api/generated';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { DepartmentModal } from '@/modules/admin-dashboard/components/modals/DepartmentModal.tsx';
import {
  useAddUsersToDepartmentMutation,
  useDeleteUsersFromDepartmentMutation,
  useGetUsersFromDepartmentQuery,
} from '@/modules/admin-dashboard/queries/department.ts';
import { useUsersListQuery } from '@/modules/admin-dashboard/queries/users.ts';
import { BaseModalProps } from 'ui';
import { useDebounce } from 'ui/hooks';

interface AddBusinessUnitModalProps extends BaseModalProps {
  departmentId: number;
  departmentName: string;
}

export type sortedUsersType = {
  pages:
    | { data: (User | UserResponse)[]; total: number; page: number; page_size: number }[]
    | undefined;
  pageParams?: unknown[] | undefined;
};

const initialValues: { name: string; userIds: number[] } = {
  name: '',
  userIds: [],
};

export const UpdateDepartmentModal: React.FC<AddBusinessUnitModalProps> = ({
  open,
  onClose,
  departmentId,
  departmentName,
}) => {
  const handleCloseModal = () => {
    formik.resetForm();
    onClose();
  };
  const [sortedUsers, setSortedUsers] = useState<sortedUsersType | undefined>(undefined);
  const [filterByName, setFilterByName] = useDebounce('', 500);
  const [excludeIds, setExcludeIds] = useState('');

  const { data: existingUsers, isFetched: isFetchedDepartmentUsers } =
    useGetUsersFromDepartmentQuery(departmentId, !departmentId);

  useEffect(() => {
    if (existingUsers?.department.users) {
      const excludeIds = existingUsers?.department.users.map(user => user.id);
      setExcludeIds(excludeIds.join(','));
    }
  }, [existingUsers]);

  const {
    hasNextPage,
    fetchNextPage,
    data: users,
    isLoading,
  } = useUsersListQuery(
    {
      page: 1,
      page_size: 15,
      filter_by_name: filterByName,
      exclude: excludeIds,
    },
    { disabled: !isFetchedDepartmentUsers },
  );

  const handleFormSubmit = async (values: { name: string; userIds: number[] }) => {
    const existingIds = existingUsers?.department?.users
      ? existingUsers.department.users.map(user => user.id)
      : [];
    const deleteUsersIds = existingIds.filter(id => !values.userIds.includes(id));
    const addUsersIds = values.userIds.filter(id => !existingIds.includes(id));
    deleteUsersIds.length &&
      (await deleteUsers({ user_ids: deleteUsersIds, department_id: departmentId }));
    addUsersIds.length &&
      (await addUsers({ user_ids: addUsersIds, department_ids: [departmentId] }));
  };

  const sortUsers = (
    users: InfiniteData<GetUserListResponse> | undefined,
    existingUsers: GetUsersByDepartmentResponse | undefined,
  ) => {
    if (!users) return undefined;

    const checkedUsers = existingUsers?.department?.users
      ? existingUsers.department.users.map(user => user)
      : [];

    const firstPage = users.pages[0];
    const noCheckedUsers = firstPage.data.filter(user => !checkedUsers.some(u => user.id === u.id));
    const updatedFirstPage = {
      ...firstPage,
      data: [...checkedUsers, ...noCheckedUsers],
    };

    const remainingPages = users.pages.slice(1);

    return {
      ...users,
      pages: [updatedFirstPage, ...remainingPages],
    };
  };

  useEffect(() => {
    setSortedUsers(sortUsers(users, existingUsers));
  }, [users, existingUsers]);

  const formik = useFormik({
    initialValues: {
      ...initialValues,
    },
    validateOnChange: false,
    validateOnMount: false,
    validateOnBlur: false,
    onSubmit: async values => {
      await handleFormSubmit(values);
      handleCloseModal();
    },
  });
  useEffect(() => {
    if (open && existingUsers?.department.users) {
      formik.setFieldValue('userIds', [
        ...existingUsers.department.users.map(user => user.id),
        ...formik.values.userIds,
      ]);
    }
  }, [existingUsers, open, departmentId, departmentName]);

  const { mutateAsync: addUsers, isLoading: isUsersAdding } = useAddUsersToDepartmentMutation({
    setErrors: formik.setErrors,
  });
  const { mutateAsync: deleteUsers, isLoading: isUsersDeletion } =
    useDeleteUsersFromDepartmentMutation({ setErrors: formik.setErrors });
  return (
    <DepartmentModal
      isLoading={isLoading}
      isSaving={isLoading || isUsersAdding || isUsersDeletion}
      isOpened={open}
      handleCloseModal={handleCloseModal}
      handleSearchChange={setFilterByName}
      fetchNextPage={fetchNextPage}
      formik={formik}
      hasNextPage={!!hasNextPage}
      users={sortedUsers}
      departmentId={departmentId}
      departmentName={departmentName}
    />
  );
};
